import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonSpinner } from '@ionic/angular/standalone';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { FormFeedbackService } from '../../services/form-feedback.service';
import {
  AccountAddress,
  AccountService,
  CreateAddressRequest,
  UpdateAddressRequest
} from '../../services/account.service';

type AddressKind = 'shipping' | 'billing' | 'other';

interface AddressFormModel {
  kind: AddressKind | '';
  label: string;
  fullName: string;
  company: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  isDefault: boolean;
}

@Component({
  selector: 'app-addresses-page',
  standalone: true,
  imports: [FormsModule, IonSpinner, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './addresses-page.component.html',
  styleUrls: ['./addresses-page.component.scss']
})
export class AddressesPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  addresses: AccountAddress[] = [];
  newAddressForm: AddressFormModel = this.createEmptyAddressForm();
  editingAddressPublicId: string | null = null;
  editAddressForm: AddressFormModel = this.createEmptyAddressForm();

  isInitialLoading = true;
  hasInitialLoadError = false;
  isAddressBusy = false;
  addressMessage = '';
  addressError = '';

  constructor(
    private readonly accountService: AccountService,
    private readonly formFeedbackService: FormFeedbackService
  ) {}

  ngOnInit(): void {
    this.loadAddresses();
  }

  createAddress(form: HTMLFormElement): void {
    this.addressMessage = '';
    this.addressError = '';

    if (!this.isAddressFormValid(this.newAddressForm)) {
      this.formFeedbackService.showInvalidFields(form, this.invalidAddressFields(this.newAddressForm, 'new'));
      return;
    }

    this.isAddressBusy = true;
    const payload: CreateAddressRequest = this.buildCreateAddressPayload(this.newAddressForm);

    this.accountService.createAddress(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            this.newAddressForm = this.createEmptyAddressForm();
            this.addressMessage = 'Address added successfully.';
            this.isAddressBusy = false;
            this.refreshAddresses();
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            this.addressError = error.message || 'Could not create address.';
            this.isAddressBusy = false;
          });
        }
      });
  }

  startEditAddress(address: AccountAddress): void {
    this.addressMessage = '';
    this.addressError = '';
    this.editingAddressPublicId = address.publicId;
    this.editAddressForm = {
      kind: address.kind,
      label: address.label || '',
      fullName: address.fullName || '',
      company: address.company || '',
      line1: address.line1,
      line2: address.line2 || '',
      city: address.city,
      state: address.state || '',
      postalCode: address.postalCode,
      country: address.country,
      phone: address.phone || '',
      isDefault: address.isDefault
    };
  }

  cancelEditAddress(): void {
    this.editingAddressPublicId = null;
    this.editAddressForm = this.createEmptyAddressForm();
  }

  saveAddressEdit(form: HTMLFormElement): void {
    if (!this.editingAddressPublicId) {
      return;
    }

    this.addressMessage = '';
    this.addressError = '';

    if (!this.isAddressFormValid(this.editAddressForm)) {
      this.formFeedbackService.showInvalidFields(form, this.invalidAddressFields(this.editAddressForm, 'edit'));
      return;
    }

    this.isAddressBusy = true;
    const payload: UpdateAddressRequest = this.buildUpdateAddressPayload(this.editAddressForm);

    this.accountService.updateAddress(this.editingAddressPublicId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            this.addressMessage = 'Address updated successfully.';
            this.isAddressBusy = false;
            this.cancelEditAddress();
            this.refreshAddresses();
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            this.addressError = error.message || 'Could not update address.';
            this.isAddressBusy = false;
          });
        }
      });
  }

  deleteAddress(addressPublicId: string): void {
    if (!window.confirm('Delete this address?')) {
      return;
    }

    this.addressMessage = '';
    this.addressError = '';
    this.isAddressBusy = true;

    this.accountService.deleteAddress(addressPublicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            this.addressMessage = 'Address deleted successfully.';
            this.isAddressBusy = false;
            this.refreshAddresses();
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            this.addressError = error.message || 'Could not delete address.';
            this.isAddressBusy = false;
          });
        }
      });
  }

  kindLabel(kind: AccountAddress['kind']): string {
    if (kind === 'shipping') {
      return 'Shipping';
    }

    if (kind === 'billing') {
      return 'Billing';
    }

    return 'Other';
  }

  loadAddresses(): void {
    this.isInitialLoading = true;
    this.hasInitialLoadError = false;
    this.addressError = '';

    this.accountService.listAddresses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (addresses) => {
          this.runInAngular(() => {
            this.addresses = addresses;
            this.isInitialLoading = false;
            this.hasInitialLoadError = false;
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            this.addressError = error.message || 'Could not load addresses.';
            this.isInitialLoading = false;
            this.hasInitialLoadError = true;
          });
        }
      });
  }

  private refreshAddresses(): void {
    this.accountService.listAddresses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (addresses) => {
          this.runInAngular(() => {
            this.addresses = addresses;
          });
        },
        error: (error: { message?: string }) => {
          this.runInAngular(() => {
            this.addressError = error.message || 'Could not refresh addresses.';
          });
        }
      });
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }

  private createEmptyAddressForm(): AddressFormModel {
    return {
      kind: '',
      label: '',
      fullName: '',
      company: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      phone: '',
      isDefault: false
    };
  }

  private isAddressFormValid(form: AddressFormModel): boolean {
    return (
      this.isAddressKind(form.kind) &&
      form.line1.trim().length > 0 &&
      form.city.trim().length > 0 &&
      form.postalCode.trim().length > 0 &&
      form.country.trim().length > 0
    );
  }

  private invalidAddressFields(form: AddressFormModel, scope: 'new' | 'edit'): string[] {
    const editSuffix = this.editingAddressPublicId ? `_${this.editingAddressPublicId}` : '';
    const fields = scope === 'new'
      ? {
        kind: 'new_address_kind',
        line1: 'new_address_line1',
        city: 'new_address_city',
        postalCode: 'new_address_postal_code',
        country: 'new_address_country',
      }
      : {
        kind: `edit_kind${editSuffix}`,
        line1: `edit_line1${editSuffix}`,
        city: `edit_city${editSuffix}`,
        postalCode: `edit_postal${editSuffix}`,
        country: `edit_country${editSuffix}`,
      };

    return [
      this.isAddressKind(form.kind) ? null : fields.kind,
      form.line1.trim() ? null : fields.line1,
      form.city.trim() ? null : fields.city,
      form.postalCode.trim() ? null : fields.postalCode,
      form.country.trim() ? null : fields.country,
    ].filter((field): field is string => field !== null);
  }

  private buildCreateAddressPayload(form: AddressFormModel): CreateAddressRequest {
    return {
      kind: this.requireAddressKind(form.kind),
      label: this.toNullable(form.label),
      fullName: this.toNullable(form.fullName),
      company: this.toNullable(form.company),
      line1: form.line1.trim(),
      line2: this.toNullable(form.line2),
      city: form.city.trim(),
      state: this.toNullable(form.state),
      postalCode: form.postalCode.trim(),
      country: form.country.trim(),
      phone: this.toNullable(form.phone),
      isDefault: form.isDefault
    };
  }

  private buildUpdateAddressPayload(form: AddressFormModel): UpdateAddressRequest {
    return {
      kind: this.requireAddressKind(form.kind),
      label: this.toNullable(form.label),
      fullName: this.toNullable(form.fullName),
      company: this.toNullable(form.company),
      line1: form.line1.trim(),
      line2: this.toNullable(form.line2),
      city: form.city.trim(),
      state: this.toNullable(form.state),
      postalCode: form.postalCode.trim(),
      country: form.country.trim(),
      phone: this.toNullable(form.phone),
      isDefault: form.isDefault
    };
  }

  private toNullable(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private isAddressKind(kind: AddressFormModel['kind']): kind is AddressKind {
    return kind === 'shipping' || kind === 'billing' || kind === 'other';
  }

  private requireAddressKind(kind: AddressFormModel['kind']): AddressKind {
    if (!this.isAddressKind(kind)) {
      throw new Error('An address kind is required.');
    }

    return kind;
  }
}
